import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import Breadcrumb from '../Breadcrumb';

interface ProjectDetailsProps {
  projectId: string;
}

const ProjectDetails: React.FC<ProjectDetailsProps> = ({ projectId }) => {
  const [project, setProject] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [updating, setUpdating] = useState(false);
  const [versions, setVersions] = useState<any[]>([]);
  const [editingVersion, setEditingVersion] = useState<string | null>(null);
  const [editVersionName, setEditVersionName] = useState('');

  useEffect(() => {
    fetchProject();
    fetchVersions();
  }, [projectId]);

  const fetchVersions = async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/projects/${projectId}/versions`, {
        headers: {
          'Authorization': token || ''
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setVersions(data.versions || []);
      }
    } catch (error) {
      console.error('Error fetching versions:', error);
    }
  };

  const fetchProject = async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/projects`, {
        headers: {
          'Authorization': token || '',
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const projects = await response.json();
        const currentProject = projects.find((p: any) => p.project_id === projectId);
        setProject(currentProject);
        setEditName(currentProject?.name || '');
        setEditDescription(currentProject?.description || '');
      }
    } catch (error) {
      console.error('Error fetching project:', error);
    }
    setLoading(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditName(project.name);
    setEditDescription(project.description);
  };

  const handleUpdate = async () => {
    if (!editName.trim() || !editDescription.trim()) return;
    
    setUpdating(true);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/projects`, {
        method: 'PUT',
        headers: {
          'Authorization': token || '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          project_id: projectId,
          name: editName.trim(),
          description: editDescription.trim()
        })
      });
      
      if (response.ok) {
        const updatedProject = await response.json();
        setProject(updatedProject);
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Error updating project:', error);
    }
    setUpdating(false);
  };

  const handleEditVersion = (version: string, name: string) => {
    setEditingVersion(version);
    setEditVersionName(name);
  };

  const handleCancelVersionEdit = () => {
    setEditingVersion(null);
    setEditVersionName('');
  };

  const handleUpdateVersion = async (version: string) => {
    if (!editVersionName.trim()) return;
    
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/projects/${projectId}/versions/${version}`, {
        method: 'PUT',
        headers: {
          'Authorization': token || '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          version_name: editVersionName.trim()
        })
      });
      
      if (response.ok) {
        // Refresh versions list
        await fetchVersions();
        setEditingVersion(null);
        setEditVersionName('');
      }
    } catch (error) {
      console.error('Error updating version:', error);
    }
  };

  if (loading) return <div className="loading">Loading project details...</div>;
  if (!project) return <div className="error">Project not found</div>;

  return (
    <div className="module-content">
      <Breadcrumb items={[
        { label: 'Home', path: '/' },
        { label: 'Projects', path: '/' },
        { label: projectId }
      ]} />
      <div className="module-header">
        <h2>Project Details</h2>
        {!isEditing && (
          <button className="edit-btn" onClick={handleEdit}>
            Edit
          </button>
        )}
      </div>
      <div className="project-info">
        <div className="info-card">
          <h3>Basic Information</h3>
          <div className="info-row">
            <label>Project ID:</label>
            <span>{project.project_id}</span>
          </div>
          <div className="info-row">
            <label>Name:</label>
            {isEditing ? (
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="edit-input"
              />
            ) : (
              <span>{project.name}</span>
            )}
          </div>
          <div className="info-row">
            <label>Description:</label>
            {isEditing ? (
              <textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                className="edit-textarea"
                rows={3}
              />
            ) : (
              <span>{project.description}</span>
            )}
          </div>
          <div className="info-row">
            <label>Created:</label>
            <span>{new Date(project.created_at).toLocaleString()}</span>
          </div>
          {isEditing && (
            <div className="edit-actions">
              <button className="cancel-btn" onClick={handleCancel}>
                Cancel
              </button>
              <button 
                className="update-btn" 
                onClick={handleUpdate}
                disabled={updating || !editName.trim() || !editDescription.trim()}
              >
                {updating ? 'Updating...' : 'Update'}
              </button>
            </div>
          )}
        </div>
        
        <div className="info-card">
          <h3>Versions</h3>
          {versions.filter(v => v.version !== 'build').length > 0 ? (
            <div className="versions-table">
              <table>
                <thead>
                  <tr>
                    <th>Version</th>
                    <th>Name</th>
                    <th>Created Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {versions.filter(v => v.version !== 'build').map((version) => (
                    <tr key={version.version}>
                      <td>v{version.version}</td>
                      <td>
                        {editingVersion === version.version ? (
                          <input
                            type="text"
                            value={editVersionName}
                            onChange={(e) => setEditVersionName(e.target.value)}
                            className="edit-input"
                          />
                        ) : (
                          version.name
                        )}
                      </td>
                      <td>{version.created_at ? new Date(version.created_at).toLocaleString() : 'N/A'}</td>
                      <td>
                        {editingVersion === version.version ? (
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button 
                              className="update-btn"
                              onClick={() => handleUpdateVersion(version.version)}
                              style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                            >
                              Update
                            </button>
                            <button 
                              className="cancel-btn"
                              onClick={() => handleCancelVersionEdit()}
                              style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button 
                            className="edit-btn"
                            onClick={() => handleEditVersion(version.version, version.name)}
                          >
                            Edit
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p>No published versions found</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectDetails;